import React from 'react';

const formatINR = (v: number) => {
    if (!isFinite(v) || isNaN(v)) return '-';
    const nf = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
    return `₹${nf.format(v)}`;
};

const formatCompact = (v: number) => {
    if (!isFinite(v) || isNaN(v)) return '-';
    if (Math.abs(v) >= 100000) {
        const lakhs = Math.round(v / 100000);
        return `₹${lakhs} L`;
    }
    return formatINR(v);
};

const RentalYieldCalculator: React.FC = () => {
    const [purchasePrice, setPurchasePrice] = React.useState<number>(250000);
    const [monthlyRent, setMonthlyRent] = React.useState<number>(1000);
    const [annualExpenses, setAnnualExpenses] = React.useState<number>(1000);

    const yearlyRent = React.useMemo(() => monthlyRent * 12, [monthlyRent]);
    const grossYield = React.useMemo(() => (purchasePrice > 0 ? (yearlyRent / purchasePrice) * 100 : 0), [yearlyRent, purchasePrice]);
    const netYield = React.useMemo(() => (purchasePrice > 0 ? ((yearlyRent - annualExpenses) / purchasePrice) * 100 : 0), [yearlyRent, annualExpenses, purchasePrice]);

    return (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 purple-card-shadow">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-semibold mb-1">Purchase Price (₹)*</label>
                        <input type="number" value={purchasePrice} onChange={(e) => setPurchasePrice(Number(e.target.value) || 0)} className="w-full border rounded p-3 text-sm" />
                        <div className="text-[12px] text-slate-500 mt-1">{formatCompact(purchasePrice)}</div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold mb-1">Monthly Rent (₹)*</label>
                        <input type="number" value={monthlyRent} onChange={(e) => setMonthlyRent(Number(e.target.value) || 0)} className="w-full border rounded p-3 text-sm" />
                        <div className="text-[12px] text-slate-500 mt-1">{formatINR(monthlyRent)}</div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold mb-1">Annual Expenses (₹)*</label>
                        <input type="number" value={annualExpenses} onChange={(e) => setAnnualExpenses(Number(e.target.value) || 0)} className="w-full border rounded p-3 text-sm" />
                        <div className="text-[12px] text-slate-500 mt-1">{formatINR(annualExpenses)}</div>
                    </div>
                </div>

                <div>
                    <div className="rounded-xl border-8 border-[#001b4a] p-6 bg-white shadow-md h-full">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-extrabold text-lg text-[#001b4a]">Total Investment</h3>
                            <div className="text-2xl font-extrabold text-[#0b3b82]">{formatCompact(purchasePrice)}</div>
                        </div>

                        <div className="border-t border-dashed border-slate-200 pt-4 space-y-3">
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-slate-700">Yearly Rental Income</div>
                                <div className="font-semibold text-slate-800">{formatINR(yearlyRent)}</div>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="text-sm text-slate-700">Gross Rental Yield</div>
                                <div className="font-semibold text-emerald-600">{isFinite(grossYield) ? `${grossYield.toFixed(1)} %` : '-'}</div>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="text-sm text-slate-700">Net Rental Yield</div>
                                <div className="font-semibold text-emerald-600">{isFinite(netYield) ? `${netYield.toFixed(1)} %` : '-'}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RentalYieldCalculator;
